/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { ClusterStates, Endpoints, Infer } from 'shared';

import { Namespaces } from '../../../../i18n';
import { SystemConfigContextProvider } from '../../../../providers/SystemConfigProvider';
import { useClusterPlanStyles } from '../../../../styles/clusterPlan';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { useControlOverlays } from '../../../../utils/useControlOverlays';
import { useProjectPermissions } from '../../../../utils/useProjectPermissions';
import { ChangeClusterPlanOverlay } from './components/ChangeClusterPlanOverlay/ChangeClusterPlanOverlay';
import { ChangePlanButton } from './components/ChangePlanButton/ChangePlanButton';
import { PlanTabContent } from './components/PlanTabContent/PlanTabContent';

type IProps = {
  cluster: Infer<typeof Endpoints.GetCluster>['Response'][200];
  maintenanceWindows: string[];
  status: ClusterStates | undefined;
  identificationData: UpdateClusterDetailsParameterIdentification;
};

export const PlanTab = memo<IProps>(props => {
  const { cluster, maintenanceWindows, identificationData, status } = props;

  const permissions = useProjectPermissions(identificationData.organizationId, identificationData.projectId);

  const { doOpenUpdateClusterOverlay, doCloseUpdateClusterOverlay } = useControlOverlays();

  const clusterConfig = useMemo(() => cluster.configuration, [cluster]);

  const classes = useClusterPlanStyles();
  const { t } = useTranslation(Namespaces.CreateCluster);
  const [isOpen, setIsOpen] = useState(false);

  const currentConfig = useMemo(() => {
    return clusterConfig?.filter(conf => conf.endedAt === 'infinity')[0];
  }, [clusterConfig]);

  const openOverlay = () => {
    doOpenUpdateClusterOverlay({ serviceType: cluster.type });
    setIsOpen(true);
  };

  const closeOverlay = () => {
    doCloseUpdateClusterOverlay();
    setIsOpen(false);
  };

  return (
    <SystemConfigContextProvider>
      <ChangeClusterPlanOverlay
        isOpen={isOpen}
        closeOverlay={closeOverlay}
        currentConfig={{ ...currentConfig, serviceType: cluster.type }}
        maintenanceWindows={maintenanceWindows}
        identificationData={identificationData}
      />
      <Grid container direction="column">
        <Grid item xs={12}>
          <div className={classes.root}>
            <div className={classes.stepTitleBlock}>
              <div className={classes.stepTitle}>{t('step_3_title')}</div>
              {permissions.edit_cluster ? (
                <ChangePlanButton
                  clusterStatus={status}
                  onClick={openOverlay}
                  disabled={!status || !maintenanceWindows.length}
                />
              ) : null}
            </div>
            <PlanTabContent cluster={cluster} status={status} currentConfig={currentConfig} />
          </div>
        </Grid>
      </Grid>
    </SystemConfigContextProvider>
  );
});
